// ============================================
// AlpenScreen AI Preferences Manager
// File: frontend/src/utils/aiPreferences.js
// Local Storage Manager for AI Settings
// ============================================

const AI_PREFERENCES_KEY = 'alpenscreen_ai_preferences';
const CONTENT_HISTORY_KEY = 'alpenscreen_content_history';
const TRANSLATION_MEMORY_KEY = 'alpenscreen_translation_memory';

// Default preferences
const DEFAULT_PREFERENCES = {
  industry: 'general', // ✅ GEÄNDERT: von 'restaurant' zu 'general'
  defaultTone: 'professional',
  language: 'de',
  customTones: [],
  favoritePrompts: [],
  batchCount: 3,
  maxHistoryItems: 20
};

// ============================================
// ✅ NEU: Industry & Tone Options
// ============================================

export const INDUSTRIES = [
  { id: 'general', name: 'Allgemein', icon: '🏢' },           // NEU - Standard
  { id: 'restaurant', name: 'Restaurant', icon: '🍽️' },
  { id: 'medical', name: 'Medizin', icon: '🏥' },
  { id: 'fitness', name: 'Fitness', icon: '💪' },
  { id: 'retail', name: 'Retail', icon: '🛍️' },
  { id: 'hotel', name: 'Hotel', icon: '🏨' },
  { id: 'real_estate', name: 'Immobilien', icon: '🏘️' },     // NEU
  { id: 'beauty_wellness', name: 'Beauty/Wellness', icon: '💆' }, // NEU
  { id: 'education', name: 'Bildung', icon: '🎓' },          // NEU
  { id: 'technology', name: 'Technologie', icon: '💻' },     // NEU
  { id: 'crafts', name: 'Handwerk', icon: '🔨' },            // NEU
  { id: 'services', name: 'Dienstleistungen', icon: '🤝' },  // NEU
  { id: 'events', name: 'Events', icon: '🎉' },              // NEU
  { id: 'automotive', name: 'Automotive', icon: '🚗' },      // NEU
  { id: 'insurance', name: 'Versicherungen', icon: '🛡️' },   // NEU
  { id: 'finance', name: 'Finanzen', icon: '💰' }            // NEU
];

export const TONES = [
  { id: 'professional', name: 'Professionell', icon: '💼', description: 'Sachlich und vertrauenswürdig' },
  { id: 'casual', name: 'Locker', icon: '😊', description: 'Freundlich und zugänglich' },
  { id: 'energetic', name: 'Energiegeladen', icon: '⚡', description: 'Dynamisch und motivierend' },
  { id: 'elegant', name: 'Elegant', icon: '✨', description: 'Raffiniert und hochwertig' }
];

// ============================================
// AI Preferences
// ============================================

export const getAIPreferences = () => {
  try {
    const stored = localStorage.getItem(AI_PREFERENCES_KEY);
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading AI preferences:', error);
  }
  return DEFAULT_PREFERENCES;
};

export const saveAIPreferences = (preferences) => {
  try {
    localStorage.setItem(AI_PREFERENCES_KEY, JSON.stringify(preferences));
    return true;
  } catch (error) {
    console.error('Error saving AI preferences:', error);
    return false;
  }
};

export const updateAIPreference = (key, value) => {
  const prefs = getAIPreferences();
  prefs[key] = value;
  return saveAIPreferences(prefs);
};

// ============================================
// Content History
// ============================================

export const getContentHistory = () => {
  try {
    const stored = localStorage.getItem(CONTENT_HISTORY_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading content history:', error);
  }
  return [];
};

export const addToHistory = (item) => {
  try {
    const history = getContentHistory();
    const newItem = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      favorite: false,
      ...item
    };
    
    // Add to beginning
    history.unshift(newItem);
    
    // Limit history size
    const prefs = getAIPreferences();
    const limited = history.slice(0, prefs.maxHistoryItems);
    
    localStorage.setItem(CONTENT_HISTORY_KEY, JSON.stringify(limited));
    return newItem;
  } catch (error) {
    console.error('Error adding to history:', error);
    return null;
  }
};

export const toggleHistoryFavorite = (id) => {
  try {
    const history = getContentHistory();
    const item = history.find(h => h.id === id);
    if (item) {
      item.favorite = !item.favorite;
      localStorage.setItem(CONTENT_HISTORY_KEY, JSON.stringify(history));
      return true;
    }
  } catch (error) {
    console.error('Error toggling favorite:', error);
  }
  return false;
};

export const deleteHistoryItem = (id) => {
  try {
    const history = getContentHistory();
    const filtered = history.filter(h => h.id !== id);
    localStorage.setItem(CONTENT_HISTORY_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting history item:', error);
    return false;
  }
};

export const clearHistory = () => {
  try {
    localStorage.setItem(CONTENT_HISTORY_KEY, JSON.stringify([]));
    return true;
  } catch (error) {
    console.error('Error clearing history:', error);
    return false;
  }
};

export const exportHistory = () => {
  try {
    const history = getContentHistory();
    const dataStr = JSON.stringify(history, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `alpenscreen_history_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    return true;
  } catch (error) {
    console.error('Error exporting history:', error);
    return false;
  }
};

// ============================================
// Translation Memory (Glossary)
// ============================================

export const getTranslationMemory = () => {
  try {
    const stored = localStorage.getItem(TRANSLATION_MEMORY_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading translation memory:', error);
  }
  return {};
};

export const addToTranslationMemory = (source, target, sourceLang, targetLang) => {
  try {
    const memory = getTranslationMemory();
    const key = `${sourceLang}_${targetLang}`;
    
    if (!memory[key]) {
      memory[key] = {};
    }
    
    memory[key][source.toLowerCase()] = target;
    localStorage.setItem(TRANSLATION_MEMORY_KEY, JSON.stringify(memory));
    return true;
  } catch (error) {
    console.error('Error adding to translation memory:', error);
    return false;
  }
};

export const getTranslationSuggestion = (text, sourceLang, targetLang) => {
  try {
    const memory = getTranslationMemory();
    const key = `${sourceLang}_${targetLang}`;
    
    if (memory[key]) {
      return memory[key][text.toLowerCase()] || null;
    }
  } catch (error) {
    console.error('Error getting translation suggestion:', error);
  }
  return null;
};

export const clearTranslationMemory = () => {
  try {
    localStorage.setItem(TRANSLATION_MEMORY_KEY, JSON.stringify({}));
    return true;
  } catch (error) {
    console.error('Error clearing translation memory:', error);
    return false;
  }
};

// ============================================
// Utility Functions
// ============================================

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (fallbackError) {
      console.error('Error copying to clipboard:', fallbackError);
      return false;
    }
  }
};

export const countCharacters = (text) => {
  if (!text) return 0;
  return text.length;
};

export const getOptimalLength = (contentType) => {
  const lengths = {
    headline: { min: 20, optimal: 40, max: 60 },
    description: { min: 50, optimal: 100, max: 200 },
    cta: { min: 10, optimal: 20, max: 40 },
    full_design: { min: 100, optimal: 250, max: 500 }
  };
  return lengths[contentType] || lengths.headline;
};